import type { BranchFilterItem } from "@repo/baseCloud";

import { BRANCHES, GridFiltersController, useFilterItemQuery } from "@repo/baseCloud";
import { Checkbox, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import type { ApplicationSetting } from "../../models";
import type { Segment, SettingGridRow } from "./settingsTree";

import { ApplicationSettingType } from "../../models";
import { DefinitionCascade } from "./components/definitionCascade";
import { DeleteSettingDialog } from "./components/deleteSettingDialog";
import { SegmentSelectors } from "./components/segmentSelectors";
import { SettingDetailsDialog } from "./components/settingDetailsDialog";
import { SettingDialog } from "./components/settingDialog";
import { SettingsGrid } from "./components/settingsGrid";
import { useDefinitionsQuery, useSettingsQuery } from "./hooks";
import { buildGridRows, buildTree, isCompanyWide, matchDefinition } from "./settingsTree";
import style from "./style.module.scss";

export function AppSettingsPage() {
	const definitions = useDefinitionsQuery();
	const settings = useSettingsQuery();
	// Every office, inactive ones too, since they can still have setting rows
	const [offices] = useFilterItemQuery<BranchFilterItem>([BRANCHES], GridFiltersController.getBranches);
	const loading = definitions.isLoading || settings.isLoading;
	const failed = definitions.isError || settings.isError;
	// Also true while the refresh button (or a save) reloads them, so the grid shows it's loading
	const fetching = definitions.isFetching || settings.isFetching;
	const refresh = () => void Promise.all([definitions.refetch(), settings.refetch()]);

	const [search, setSearch] = useState("");
	const [path, setPath] = useState<string[]>([]);
	const [leaf, setLeaf] = useState<string>();
	const [specifiedOnly, setSpecifiedOnly] = useState(false);
	const [orphanedOnly, setOrphanedOnly] = useState(false);
	const [segment, setSegment] = useState<Segment>({});
	const [pertinentOnly, setPertinentOnly] = useState(false);
	/** The open setting dialog: name is set for Add override, and setting for Edit */
	const [dialog, setDialog] = useState<{ name?: string; setting?: ApplicationSetting }>();
	/** The row waiting for its delete to be confirmed */
	const [deleting, setDeleting] = useState<ApplicationSetting>();
	/** The setting whose details are open */
	const [details, setDetails] = useState<string>();
	const deferredSearch = useDeferredValue(search);
	const addOverride = useCallback((row: SettingGridRow) => setDialog({ name: row.name }), []);
	const edit = useCallback((setting: ApplicationSetting) => setDialog({ setting }), []);
	const openDetails = useCallback((row: SettingGridRow) => setDetails(row.name), []);

	const tree = useMemo(() => buildTree(definitions.data ?? []), [definitions.data]);
	const hasDefinitions = tree.nodes.size > 0;
	const deletingDefinition = deleting && matchDefinition(tree, deleting.name)?.node.definition;

	const rows = useMemo(
		() =>
			buildGridRows(tree, settings.data ?? [], {
				path,
				leaf,
				search: deferredSearch,
				specifiedOnly,
				orphanedOnly,
				pertinentOnly,
				segment,
				offices: offices.data,
			}),
		[tree, settings.data, path, leaf, deferredSearch, specifiedOnly, orphanedOnly, pertinentOnly, segment, offices],
	);
	// Without Pertinent only, the segment changes the values but not which settings are listed, so it doesn't reset the page
	const filterKey = JSON.stringify([
		path,
		leaf,
		deferredSearch,
		specifiedOnly,
		orphanedOnly,
		pertinentOnly && segment,
	]);

	return (
		<Loading loading={loading}>
			<div className={style.page}>
				<h2 className={style.title}>Application Settings</h2>
				{failed && <p className={style.error}>The application settings couldn't be loaded.</p>}
				{!loading && !failed && !hasDefinitions && (
					<p className={style.warning}>
						No setting definitions were found on this database, so settings can't be matched to their
						definitions or defaults.
					</p>
				)}
				<FiltersV2>
					<TextBox
						label="Search"
						placeholder="Name, friendly name or description"
						value={search}
						changeHandler={(value) => setSearch(value ?? "")}
					/>
					<div className={style.checkboxes}>
						<Checkbox
							id="appSettingsSpecifiedOnly"
							title="Specified only"
							label=""
							value={specifiedOnly}
							changeHandler={(checked: boolean) => setSpecifiedOnly(checked)}
							inline={false}
						/>
						{/* With no definitions every row would count as orphaned, so the filter is hidden */}
						{hasDefinitions && (
							<Checkbox
								id="appSettingsOrphanedOnly"
								title="Orphaned only"
								label=""
								value={orphanedOnly}
								changeHandler={(checked: boolean) => setOrphanedOnly(checked)}
								inline={false}
							/>
						)}
					</div>
					<DefinitionCascade
						tree={tree}
						path={path}
						leaf={leaf}
						onChange={(newPath, newLeaf) => {
							setPath(newPath);
							setLeaf(newLeaf);
						}}
					/>
				</FiltersV2>
				<FiltersV2>
					<SegmentSelectors segment={segment} offices={offices.data} onChange={setSegment} />
					<Checkbox
						id="appSettingsPertinentOnly"
						title="Pertinent only"
						label=""
						value={pertinentOnly}
						changeHandler={(checked: boolean) => setPertinentOnly(checked)}
						inline={false}
					/>
				</FiltersV2>
				<p className={style.count}>
					{rows.length} {rows.length === 1 ? "setting" : "settings"}, with{" "}
					{isCompanyWide(segment) ? "company-wide values" : "the values for the selected segment"}
				</p>
				<SettingsGrid
					rows={rows}
					loading={fetching}
					filterKey={filterKey}
					segment={segment}
					orphanedOnly={orphanedOnly}
					onAdd={() => setDialog({})}
					onAddOverride={addOverride}
					onEdit={edit}
					onDelete={setDeleting}
					onRefresh={refresh}
					onOpen={openDetails}
				/>
				{/* Before the other dialogs, so the ones it opens show on top of it */}
				{details && (
					<SettingDetailsDialog
						key={details}
						tree={tree}
						settings={settings.data ?? []}
						segment={segment}
						name={details}
						onClose={() => setDetails(undefined)}
						onEdit={edit}
						onDelete={setDeleting}
						onAddOverride={(name) => setDialog({ name })}
					/>
				)}
				{dialog && (
					<SettingDialog
						tree={tree}
						offices={offices.data}
						setting={dialog.setting}
						name={dialog.name}
						segment={segment}
						onClose={() => setDialog(undefined)}
					/>
				)}
				{deleting && (
					<DeleteSettingDialog
						setting={deleting}
						enumOptions={
							deletingDefinition?.dataType === ApplicationSettingType.Enum
								? deletingDefinition.enumOptions
								: undefined
						}
						onClose={() => setDeleting(undefined)}
					/>
				)}
			</div>
		</Loading>
	);
}
